import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Wraps long-form SEO body content.
 *
 * When `collapsible` is true, the block is visually clamped to ~3-6 lines
 * with a "Read more" / "Show less" toggle. The full content is ALWAYS
 * rendered into the DOM — collapse is a pure-CSS `max-height` transition —
 * so crawlers index the full text and the page keeps its SERP weight.
 * (Ref: Google mobile-first indexing guidance on expandable content.)
 */
const SeoContentBlock = ({ children, collapsible = false }) => {
  const [expanded, setExpanded] = useState(false);
  const clamped = collapsible && !expanded;

  return (
    <section
      className="mt-12 mb-4 border-t border-slate-200 pt-10"
      data-testid="seo-content-block"
    >
      <div className="max-w-4xl mx-auto px-4">
        <div
          id="seo-content-block-content"
          className={`prose prose-slate prose-sm max-w-none text-slate-500 leading-relaxed [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-slate-700 [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-slate-600 [&_h3]:mt-4 [&_h3]:mb-1 [&_p]:mb-3 [&_a]:text-[#0F2C3E] [&_a]:underline [&_a]:decoration-slate-300 hover:[&_a]:decoration-[#0F2C3E] [&_a]:transition-colors ${
            collapsible
              ? `relative overflow-hidden transition-[max-height] duration-300 ease-out ${
                  clamped ? 'max-h-32 md:max-h-56' : 'max-h-[4000px]'
                }`
              : ''
          }`}
        >
          {children}
          {clamped && (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent"
              aria-hidden="true"
            />
          )}
        </div>
        {collapsible && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls="seo-content-block-content"
            className="mt-3 text-sm font-semibold text-blue-700 hover:text-blue-800 underline-offset-2 hover:underline"
            data-testid="seo-content-block-toggle"
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>
    </section>
  );
};

const SeoLink = ({ to, children }) => (
  <Link to={to}>{children}</Link>
);

export { SeoContentBlock, SeoLink };
